import { Button } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppBar, Dialog, DialogActions, DialogContent, Toolbar } from '@mui/material';
import InputField from 'components/fields/InputField';
import Error from 'components/message/Error';
import MuiDateTimePicker from 'components/MuiDateTimePicker';
import MuiSelect from 'components/MuiSelect';
import { CloseModalButton } from 'components/Utils';
import dayjs from 'dayjs';
import useDataFetching from 'hooks/useDataFetching';
import usePostAxiosData from 'hooks/usePostAxiosData';
import React, { useEffect, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // Importer useTranslation
import { config } from 'variables/config';
import * as yup from 'yup';

const AddSpyEnterprise = ({ isOpen, onClose, get, referenceReport }) => {
  const { t } = useTranslation('add_spy_enterprise'); // Utiliser le namespace 'addSpyEnterprise'

  // Schéma de validation Yup, traduit
  const schema = yup.object().shape({
    socialRaison: yup.string().required(t('social_raison_required')),
    sigle: yup.string(),
    town: yup.string().required(t('town_required')),
    locality: yup.string().required(t('locality_required')),
    groupNumber: yup.number().positive(t('group_number_positive')).nullable(),
    employeesNumber: yup.number().positive(t('employees_number_positive')).nullable(),
    files: yup
      .mixed()
      .test('fileCount', t('file_count_limit'), (value) => !value || value.length <= 10),
    longitude: yup
      .number()
      .min(-180, t('longitude_valid'))
      .max(180, t('longitude_valid'))
      .nullable(),
    latitude: yup.number().min(-90, t('latitude_valid')).max(90, t('latitude_valid')).nullable(),
    employeeSituation: yup.string(),
    observation: yup.string(),
    employeeStatus: yup.string(),
    referenceReport: yup.number().nullable(),
  });

  const [region, setRegion] = useState(null);
  const [department, setDepartment] = useState(null);
  const [arrondissement, setArrondissement] = useState(null);
  const [files, setFiles] = useState([]);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [arrivalTime, setArrivalTime] = useState(null);

  const auth = useAuthUser();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue, // Keep setValue if needed elsewhere for initial values
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [loadingR, errorR, regions] = useDataFetching(`${config.app.api_url}/regions`);
  const [loadingD, errorD, departments] = useDataFetching(
    region ? `${config.app.api_url}/regions/${region}/departments` : null
  );
  const [loadingA, errorA, arrondissements] = useDataFetching(
    department ? `${config.app.api_url}/departments/${department}/arrondissements` : null
  );

  const [loading, postAxiosData, errorMassage, successMessage, result] = usePostAxiosData(
    `reports/doc/${parseInt(referenceReport.id)}/add-enterprise`,
    'POST',
    true,
    'multipart/form-data'
  );

  const formatDateTime = (dateObj) =>
    dateObj ? dayjs(dateObj).format('YYYY-MM-DD HH:mm:ss') : null;

  const handleSave = async (dataYub) => {
    const data = {
      ...dataYub,
      region,
      department,
      arrondissement,
      creator: auth()?.user?.id,
      referenceReport: referenceReport?.id || null, // Ensure referenceReport.id is passed
      latitude: latitude ? parseFloat(latitude) : null,
      longitude: longitude ? parseFloat(longitude) : null,
      arrivalTime: formatDateTime(arrivalTime),
      files: files.length > 0 ? files : null,
    };

    let formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        if (key === 'files' && files && files.length > 0) {
          Array.from(files).forEach((file) => formData.append('files', file));
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    await postAxiosData(formData);
  };

  const handleReset = () => {
    setRegion(null);
    setDepartment(null);
    setArrondissement(null);
    setFiles([]);
    setLatitude('');
    setLongitude('');
    setArrivalTime(null);
    reset({
      socialRaison: '',
      sigle: '',
      town: '',
      locality: '',
      groupNumber: '',
      employeesNumber: '',
      longitude: '',
      latitude: '',
      employeeSituation: '',
      observation: '',
      employeeStatus: '',
      referenceReport: referenceReport?.id || null, // Ensure referenceReport.id is passed
      creator: auth()?.user?.id || '',
      files: [],
    });
  };

  const handleCloseInside = () => {
    handleReset();
    onClose();
  };

  useEffect(() => {
    if (successMessage?.status === 200 || successMessage?.status === 201) {
      handleCloseInside();
      get();
      toast.success(
        `${
          successMessage?.message != null ? successMessage?.message : t('success_message') // Traduit le message de succès par défaut
        } `,
        {
          duration: 4000,
          className: 'bg-success text-white',
        }
      );
    }
  }, [successMessage, t]); // Ajouter t aux dépendances

  useEffect(() => {
    // Set initial value for referenceReport if it exists when the modal opens or referenceReport changes
    if (referenceReport) {
      setValue('referenceReport', referenceReport.id);
    }
  }, [referenceReport, setValue]);

  return (
    <Dialog fullWidth maxWidth="md" aria-labelledby="customized-dialog-title" open={isOpen}>
      <form onSubmit={handleSubmit(handleSave)}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar className="bg-gray-50 text-green-500 flex justify-between items-center">
            <h1 className="text-2xl font-bold">{t('add_spy_enterprise')}</h1> {/* Traduit */}
            <h2>{referenceReport?.date}</h2> {/* Garde la date du rapport original */}
            <CloseModalButton iconColor={'red'} handleClose={handleCloseInside} />
          </Toolbar>
        </AppBar>
        <DialogContent className="flex flex-col items-center justify-between gap-4" dividers>
          {errorMassage?.message && (
            <div className="flex flex-col items-center justify-between mb-4">
              <Error message={errorMassage?.message} />
            </div>
          )}
          <div className="my-4 grid gap-2 md:grid-cols-2 w-full">
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('social_raison')}
              required
              placeholder={t('social_raison')}
              id="socialRaison"
              name="socialRaison"
              type="text"
              error={errors.socialRaison?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('sigle')}
              placeholder={t('sigle')}
              id="sigle"
              name="sigle"
              type="text"
              error={errors.sigle?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('town')}
              required
              placeholder={t('town')}
              id="town"
              name="town"
              type="text"
              error={errors.town?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('locality')}
              required
              placeholder={t('locality')}
              id="locality"
              name="locality"
              type="text"
              error={errors.locality?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('group_number')}
              placeholder={t('group_number')}
              id="groupNumber"
              name="groupNumber"
              type="number"
              error={errors.groupNumber?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('employees_number')}
              placeholder={t('employees_number')}
              id="employeesNumber"
              name="employeesNumber"
              type="number"
              error={errors.employeesNumber?.message || ''}
            />
            <InputField
              label={t('longitude')}
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              name="longitude"
              id="longitude"
              type="number"
              extra="mb-3"
              error={errors.longitude?.message || ''}
            />
            <InputField
              label={t('latitude')}
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              name="latitude"
              id="latitude"
              type="number"
              extra="mb-3"
              error={errors.latitude?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('employee_situation')}
              placeholder={t('employee_situation')}
              id="employeeSituation"
              name="employeeSituation"
              type="text"
              error={errors.employeeSituation?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('observation')}
              placeholder={t('observation')}
              id="observation"
              name="observation"
              type="text"
              error={errors.observation?.message || ''}
            />
            <InputField
              register={register}
              variant="auth"
              extra="mb-3"
              label={t('employee_status')}
              placeholder={t('employee_status')}
              id="employeeStatus"
              name="employeeStatus"
              type="text"
              error={errors.employeeStatus?.message || ''}
            />
            <MuiSelect
              label={t('region')}
              value={region}
              name="region"
              options={regions.data}
              format={{ value: 'id', label: 'name' }}
              onChange={(e) => setRegion(e.target.value)}
              emptyLabel={t('none')}
              error={errors.region?.message || ''}
            />
            <MuiSelect
              label={t('department')}
              value={department}
              options={departments.data}
              name="department"
              format={{ value: 'id', label: 'name' }}
              onChange={(e) => setDepartment(e.target.value)}
              emptyLabel={t('none')}
              error={errors.department?.message || ''}
            />
            <MuiSelect
              label={t('arrondissement')}
              value={arrondissement}
              options={arrondissements.data}
              name="arrondissement"
              format={{ value: 'id', label: 'name' }}
              onChange={(e) => setArrondissement(e.target.value)}
              emptyLabel={t('none')}
              error={errors.arrondissement?.message || ''}
            />
            <MuiDateTimePicker
              label={t('arrival_time')}
              name="arrivalTime"
              value={arrivalTime}
              onChange={(newValue) => setArrivalTime(newValue)}
              fullWidth
            />

            {/* Hidden creator field */}
            <input
              type="hidden"
              name="creator"
              value={auth()?.user?.id || ''}
              {...register('creator')}
            />
            {/* Hidden referenceReport field if provided */}
            {referenceReport && (
              <input
                type="hidden"
                name="referenceReport"
                value={referenceReport.id}
                {...register('referenceReport')}
              />
            )}
            <div className="flex flex-col items-center justify-center gap-4 w-full bg-orange-50 p-4 rounded-md shadow-md">
              <label
                htmlFor="files-upload"
                className="cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-green-300 rounded-lg p-6 hover:bg-green-400 hover:border-white text:gray-400 hover:text-white transition duration-300"
              >
                <span className="text-sm ">{t('click_to_upload')}</span> {/* Traduit */}
              </label>
              <input
                id="files-upload"
                type="file"
                name="files"
                accept=".pdf,.jpeg,.jpg,.png"
                multiple
                onChange={(e) => setFiles(e.target.files)}
              />
              {errors?.files?.message && (
                <p className="text-sm text-red-500 mt-2">{errors.files.message}</p>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="flex justify-between">
          <Button onClick={handleCloseInside}>{t('cancel')}</Button> {/* Traduit */}
          <Button type="submit" colorScheme="green" isLoading={loading}>
            {t('save')} {/* Traduit */}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddSpyEnterprise;
